import type { ResultType } from '../../../constants/global';
import { useSagaStatus } from '../../../hooks/useSagaStatus';
import { SAGA_LEVEL_BY_RESULT_TYPE } from '../../../interface/saga-status';
import { SagaPipelinePanel } from './components';
import { canTriggerAi } from './engine';

/**
 * Live pipeline view for the result the agent has just submitted.
 *
 * Mount this only while the success stage is actually on screen. The upload-result panel it
 * lives in stays mounted behind every other panel, so the caller wraps this in the existing
 * `Hvc removeDOM` and passes `enabled` — otherwise the poll would run for the whole session.
 */
export const SubmissionStatus = ({
	resultType,
	electionId,
	code,
	referenceId,
	enabled,
	onCompare,
	onReupload,
}: {
	resultType: ResultType;
	electionId: string | number | undefined;
	code: string | undefined;
	referenceId?: string;
	enabled: boolean;
	onCompare?: () => void;
	onReupload?: () => void;
}) => {
	const level = SAGA_LEVEL_BY_RESULT_TYPE[resultType] ?? 'pu';
	const { view, row, phase, isWatching, refresh, queueAiRetry, retryState } = useSagaStatus({
		level,
		electionId,
		code,
		enabled,
	});

	return (
		<SagaPipelinePanel
			view={view}
			referenceId={referenceId}
			isWatching={isWatching}
			isOffline={phase === 'offline'}
			onRefresh={refresh}
			retryState={retryState}
			// Only offered when the pipeline would actually act on it — the trigger endpoint
			// answers 202 either way, so an ungated button would report success and do nothing.
			canRetryAi={canTriggerAi(row)}
			onRetryAi={queueAiRetry}
			onAction={(kind) => {
				if (kind === 'compare') onCompare?.();
				if (kind === 'reupload') onReupload?.();
			}}
		/>
	);
};
